import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  GET_CREDIT_ESTIMATE,
  GetCreditEstimateQuery,
  GetCreditEstimateQueryVariables,
  Form6765Values,
  MissingValue,
} from '@neo.tax/shared';

import { CREDIT_CALCULATION_SERIALIZATION_KEY } from '../constants';
import { useRdContext } from '../context';

export const useCreditEstimate = ():
  | {
      form6765Values: null;
      missingValue: null;
      loading: true;
    }
  | {
      form6765Values: Omit<
        Form6765Values,
        'formLines' | 'isPayrollCreditEligible' | 'isReducedCredit' | 'recommendedFilingMethod' | 'year'
      >;
      missingValue: null;
      loading: false;
    }
  | {
      form6765Values: null;
      missingValue: MissingValue;
      loading: false;
    } => {
  const { fiscalYearId } = useRdContext();
  const { data, error, loading } = useQuery<GetCreditEstimateQuery, GetCreditEstimateQueryVariables>(
    GET_CREDIT_ESTIMATE,
    {
      variables: { fiscalYearId },
      fetchPolicy: 'network-only', // always re-fetch the credit estimate because users may have re-allocated R&D percentage elsewhere
      nextFetchPolicy: 'cache-first', // after initial fetch, observe cache updates (e.g. optimistic reduced credit recalculation)
      context: { serializationKey: CREDIT_CALCULATION_SERIALIZATION_KEY },
    },
  );

  if (loading) {
    return {
      form6765Values: null,
      missingValue: null,
      loading: true,
    };
  }

  if (error) {
    throw error;
  }

  if (!data) {
    throw createError('MissingCreditEstimateError', 'Cannot load credit estimate');
  }

  if (data.getCreditEstimate.__typename === 'Form6765Values') {
    return {
      form6765Values: data.getCreditEstimate,
      missingValue: null,
      loading,
    };
  }
  if (data.getCreditEstimate.__typename === 'MissingValue') {
    return {
      form6765Values: null,
      missingValue: data.getCreditEstimate,
      loading,
    };
  }
  throw createError('InvalidCreditEstimateType', `The typename is invalid: ${data.getCreditEstimate.__typename}`);
};
